// DOC-DEPS: LLM.md -> docs/CURRENT_TASK.md -> src/features/report/README.md
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RootPathRenderItem } from '../../../lib/rootRenderer';
import type { PlantCategoryKey } from '../../../types/plant';
import { buildSoilLegendItems } from './soilLegend';
import {
  clampViewportOffset,
  computeFocusOffset,
  computeRootCanvasLayout,
} from './soilCanvasViewport';
import { RootDetailBubble } from './RootDetailBubble';
import { RootSystem } from './RootSystem';

interface SoilCanvasProps {
  items: RootPathRenderItem[];
  selectedRootId: string | null;
  onSelectRoot: (id: string) => void;
  directionOrder: PlantCategoryKey[];
  detailBubble: {
    title: string;
    activity: string;
    category: string;
    timeRange: string;
    duration: string;
    focus: string;
  } | null;
  onCloseDetail: () => void;
}

function toCategoryKey(category: PlantCategoryKey): string {
  switch (category) {
    case 'work_study':
      return 'plant_category_work_study';
    case 'exercise':
      return 'plant_category_exercise';
    case 'social':
      return 'category_social';
    case 'entertainment':
      return 'category_entertainment';
    default:
      return 'category_life';
  }
}

const TOOLTIP_WIDTH = 264;
const TOOLTIP_MIN_MARGIN = 10;

const SoilCanvasImpl: React.FC<SoilCanvasProps> = ({
  items,
  selectedRootId,
  onSelectRoot,
  directionOrder,
  detailBubble,
  onCloseDetail,
}) => {
  const { t } = useTranslation();
  const scale = 1;
  const [viewportOffset, setViewportOffset] = useState({ x: 0, y: 0 });
  const [isActive, setIsActive] = useState(false);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (canvasRef.current && !canvasRef.current.contains(e.target as Node)) {
        setIsActive(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || typeof ResizeObserver === 'undefined') return;
    const node = canvasRef.current;
    const updateSize = () => {
      setCanvasSize({
        width: node.clientWidth,
        height: node.clientHeight,
      });
    };
    updateSize();
    const observer = new ResizeObserver(() => updateSize());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const selectedItem = useMemo(
    () => items.find(item => item.segment.id === selectedRootId) ?? null,
    [items, selectedRootId],
  );
  const rootLayout = useMemo(() => computeRootCanvasLayout(canvasSize), [canvasSize]);

  const mapRootPointToCanvas = (point: { x: number; y: number }) => ({
    x: rootLayout.left + point.x * rootLayout.scale,
    y: rootLayout.top + point.y * rootLayout.scale,
  });

  useEffect(() => {
    setViewportOffset(prev => {
      const next = clampViewportOffset(prev, canvasSize, scale);
      if (Math.abs(prev.x - next.x) < 0.01 && Math.abs(prev.y - next.y) < 0.01) {
        return prev;
      }
      return next;
    });
  }, [canvasSize, scale]);

  useEffect(() => {
    if (!selectedItem || canvasSize.width === 0 || canvasSize.height === 0) {
      return;
    }
    const focusPoint = mapRootPointToCanvas(selectedItem.tip);
    setViewportOffset(prev => {
      const next = computeFocusOffset(focusPoint, canvasSize, scale);
      if (Math.abs(prev.x - next.x) < 0.5 && Math.abs(prev.y - next.y) < 0.5) {
        return prev;
      }
      return next;
    });
  }, [canvasSize, rootLayout, scale, selectedItem]);

  const tooltipStyle = useMemo(() => {
    if (!selectedItem || !detailBubble || canvasSize.width === 0 || canvasSize.height === 0) {
      return null;
    }
    const mappedPoint = mapRootPointToCanvas(selectedItem.tip);
    const baseX = mappedPoint.x;
    const baseY = mappedPoint.y;
    const scaledX = canvasSize.width / 2 + (baseX - canvasSize.width / 2) * scale + viewportOffset.x;
    const scaledY = canvasSize.height / 2 + (baseY - canvasSize.height / 2) * scale + viewportOffset.y;
    const minLeft = TOOLTIP_MIN_MARGIN;
    const maxLeft = Math.max(minLeft, canvasSize.width - TOOLTIP_WIDTH - TOOLTIP_MIN_MARGIN);
    const left = Math.min(maxLeft, Math.max(minLeft, scaledX + 14));
    const showAbove = scaledY > 118;
    const top = showAbove ? scaledY - 12 : scaledY + 18;
    return {
      left,
      top,
      showAbove,
    };
  }, [canvasSize.height, canvasSize.width, detailBubble, rootLayout, scale, selectedItem, viewportOffset.x, viewportOffset.y]);

  const legendItems = useMemo(() => buildSoilLegendItems(directionOrder), [directionOrder]);

  return (
    <div
      ref={canvasRef}
      className="relative overflow-hidden w-full h-full select-none"
      style={{
        backgroundImage: 'url(/assets/soil.png)',
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center top',
      }}
      onClick={() => setIsActive(true)}
    >
      <div
        className="absolute inset-0 origin-center will-change-transform"
        style={{ transform: `translate3d(${viewportOffset.x}px, ${viewportOffset.y}px, 0) scale(${scale})` }}
      >
        <div
          className="absolute"
          style={{
            height: rootLayout.height,
            left: rootLayout.left,
            top: rootLayout.top,
            width: rootLayout.width,
          }}
        >
          <RootSystem items={items} selectedRootId={selectedRootId} onSelectRoot={onSelectRoot} />
        </div>
      </div>

      {detailBubble && tooltipStyle ? (
        <div
          className="pointer-events-none absolute z-20"
          style={{
            left: `${tooltipStyle.left}px`,
            top: `${tooltipStyle.top}px`,
            width: `${TOOLTIP_WIDTH}px`,
            transform: tooltipStyle.showAbove ? 'translateY(-100%)' : 'translateY(0)',
          }}
        >
          <RootDetailBubble
            title={detailBubble.title}
            activity={detailBubble.activity}
            category={detailBubble.category}
            timeRange={detailBubble.timeRange}
            duration={detailBubble.duration}
            focus={detailBubble.focus}
            onClose={onCloseDetail}
            className="pointer-events-auto rounded-xl border border-amber-200/90 bg-amber-50/95 p-3 shadow-lg"
          />
        </div>
      ) : null}

      {isActive && (
        <div className="pointer-events-none absolute bottom-3 right-3 z-10 max-w-[72%] p-2">
          <div className="space-y-1">
            {legendItems.map((item) => (
              <div
                key={item.slotKey}
                className="app-badge flex items-center gap-1.5 rounded-md bg-white/62 px-1.5 py-1 leading-none text-[#5a4028]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#5a4028]" />
                <span>{t(item.positionKey)}</span>
                <span>·</span>
                <span className="truncate">{t(toCategoryKey(item.category))}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const SoilCanvas = memo(SoilCanvasImpl);
